import React, { useEffect, useRef, useState } from 'react';
import useGhostCursorStore from './useGhostCursorStore';
import useCanvasStore from './useCanvasStore';

const RESUME_IDLE_MS = 2500;
const ENTRY_GLIDE_MS = 900;
const EXIT_GLIDE_MS = 900;
const WAVE_DURATION_MS = 1300;
const CURSOR_OFFSET_X = -6;
const CURSOR_OFFSET_Y = -4;

const offscreenPos = rect => ({ x: rect.width + 120, y: rect.height * 0.5 });
const greetingPos = rect => ({ x: Math.max(40, rect.width - 160), y: rect.height * 0.38 });
const waitingPos = rect => ({ x: Math.max(40, rect.width - 180), y: 70 });

const CURSOR_COLOR = '#7B61FF';
const NAME = 'Workflow Agent';

export default function GhostCursorOverlay({ aiChatOpen, reactFlowInstance, wrapperRef }) {
  const targetComponentId = useGhostCursorStore(s => s.targetComponentId);
  const actionLabel = useGhostCursorStore(s => s.actionLabel);
  const lastUpdateAt = useGhostCursorStore(s => s.lastUpdateAt);

  const [mode, setMode] = useState('hidden');
  const [outerPos, setOuterPos] = useState(null);
  const [instantJump, setInstantJump] = useState(false);
  const innerRef = useRef(null);
  const modeRef = useRef('hidden');
  const shownRef = useRef(false);
  const waveStartRef = useRef(0);
  const wrapperRectRef = useRef(null);

  // Keep wrapper rect cached so the per-frame follow loop doesn't force
  // synchronous layout on every tick.
  useEffect(() => {
    const el = wrapperRef?.current;
    if (!el) return undefined;
    const update = () => {
      if (wrapperRef.current) {
        wrapperRectRef.current = wrapperRef.current.getBoundingClientRect();
      }
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('scroll', update, true);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', update, true);
    };
  }, [wrapperRef]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // Entry / exit sequence driven by aiChatOpen
  useEffect(() => {
    if (!wrapperRef?.current) return undefined;
    const rect = wrapperRef.current.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return undefined;

    const timers = [];
    let raf;

    if (aiChatOpen) {
      shownRef.current = true;
      setInstantJump(true);
      setOuterPos(offscreenPos(rect));
      setMode('entering');
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          setInstantJump(false);
          setOuterPos(greetingPos(rect));
        });
      });
      timers.push(
        setTimeout(() => {
          waveStartRef.current = performance.now();
          setMode('waving_hello');
        }, ENTRY_GLIDE_MS)
      );
      timers.push(
        setTimeout(() => {
          if (wrapperRef.current) {
            setOuterPos(waitingPos(wrapperRef.current.getBoundingClientRect()));
          }
          setMode('waiting');
        }, ENTRY_GLIDE_MS + WAVE_DURATION_MS)
      );
    } else if (shownRef.current) {
      setMode('exiting');
      setOuterPos(offscreenPos(rect));
      timers.push(
        setTimeout(() => {
          shownRef.current = false;
          setOuterPos(null);
          setMode('hidden');
        }, EXIT_GLIDE_MS)
      );
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [aiChatOpen, wrapperRef]);

  // Tool activity → 'acting' briefly, then return to waiting
  useEffect(() => {
    if (!lastUpdateAt || !shownRef.current) return undefined;
    setMode('acting');
    const t = setTimeout(() => {
      if (wrapperRef.current) {
        setOuterPos(waitingPos(wrapperRef.current.getBoundingClientRect()));
      }
      setMode('waiting');
    }, RESUME_IDLE_MS);
    return () => clearTimeout(t);
  }, [lastUpdateAt, wrapperRef]);

  // Acting: follow the target component in real time
  useEffect(() => {
    if (mode !== 'acting' || !targetComponentId || !reactFlowInstance || !wrapperRef?.current) {
      return undefined;
    }
    let raf;
    const setPosWithThreshold = next => {
      setOuterPos(prev => {
        if (prev && Math.abs(prev.x - next.x) < 0.5 && Math.abs(prev.y - next.y) < 0.5) {
          return prev;
        }
        return next;
      });
    };
    const tick = () => {
      const nodes = useCanvasStore.getState().nodes;
      const node = nodes.find(n => n.id === targetComponentId);
      const toScreen = reactFlowInstance.flowToScreenPosition;
      const rect = wrapperRectRef.current || wrapperRef.current?.getBoundingClientRect();
      if (!rect) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (!node || typeof toScreen !== 'function') {
        // Node went away mid-session (e.g. just deleted) — fall back to the
        // waiting spot so the cursor doesn't stick at a stale position.
        setPosWithThreshold(waitingPos(rect));
        raf = requestAnimationFrame(tick);
        return;
      }
      const width = node.width || 220;
      const height = node.height || 60;
      const client = toScreen({
        x: node.position.x + width / 2,
        y: node.position.y + height / 2,
      });
      setPosWithThreshold({ x: client.x - rect.left, y: client.y - rect.top });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, targetComponentId, reactFlowInstance, wrapperRef]);

  // Inner layer: micro-wobble always, plus wave rotation during hello/goodbye
  useEffect(() => {
    let raf;
    const loop = () => {
      const el = innerRef.current;
      if (el) {
        const t = performance.now() / 1000;
        const wx = Math.sin(t * 0.9) * 4 + Math.sin(t * 0.31 + 1.2) * 2;
        const wy = Math.cos(t * 0.7) * 3 + Math.cos(t * 0.22 + 0.5) * 2;
        let rot = 0;
        if (modeRef.current === 'waving_hello') {
          const elapsed = performance.now() - waveStartRef.current;
          const progress = Math.min(1, elapsed / WAVE_DURATION_MS);
          const damp = Math.max(0.15, 1 - progress * 0.85);
          rot = Math.sin(elapsed * 0.028) * 28 * damp;
        }
        el.style.transformOrigin = '4px 4px';
        el.style.transform = `rotate(${rot}deg) translate(${wx}px, ${wy}px)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!outerPos) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'visible',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transform: `translate(${outerPos.x + CURSOR_OFFSET_X}px, ${outerPos.y + CURSOR_OFFSET_Y}px)`,
          transition: instantJump
            ? 'none'
            : 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'transform',
        }}
      >
        <div ref={innerRef} style={{ willChange: 'transform' }}>
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))', display: 'block' }}
          >
            <path
              d="M5 2.5 L5 17.5 L9.2 13.5 L11.8 18.7 L14.3 17.6 L11.7 12.5 L17 12 Z"
              fill={CURSOR_COLOR}
              stroke="#ffffff"
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </svg>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: 20,
              background: CURSOR_COLOR,
              color: '#ffffff',
              padding: '3px 8px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.22)',
              letterSpacing: 0.1,
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            {NAME}
            {mode === 'acting' && actionLabel ? (
              <span style={{ opacity: 0.8, fontWeight: 500, marginLeft: 6 }}>
                · {actionLabel}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
